import React, { useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { BuildingSummary } from '../../../types/protocols';
import { Userprofile } from '../../../Helpers/UsersContext';
import { getChartColor } from '../../../Helpers/chartColors';
import { BuildingServicesData } from '../../../hooks/analysis/useYearComparison';
import { useRoutes } from '../../../hooks/analysis/useRoutes';
import { useRoutePlayback } from '../../../hooks/analysis/useRoutePlayback';
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen';
import { UserGroup } from '../types/analysis.types';
import { adjacentDay, frameAt, LatLng, playbackDurationMs, routeTimeline } from '../utils/routes';
import RoutesMap, { Replay, SelectedStop } from './RoutesMap';
import RouteStopList, { VisibleRoute } from './RouteStopList';

interface RoutesViewProps {
    isLoading: boolean;
    servicesData: BuildingServicesData | null;
    buildings: BuildingSummary[];
    users: Userprofile[];
    userGroups: UserGroup[];
    /** Service type key → title */
    serviceTitles: Record<string, string>;
}

/** Resvägar: the routes a company's people took on one day */
const RoutesView: React.FC<RoutesViewProps> = ({ isLoading, servicesData, buildings, users, userGroups, serviceTitles }) => {
    const sortedGroups = useMemo(
        () => [...userGroups].sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        [userGroups],
    );
    const [pickedGroupKey, setPickedGroupKey] = useState<string | null>(null);
    const groupKey = pickedGroupKey ?? sortedGroups[0]?.key ?? null;
    // null = the company's latest day with logs
    const [pickedDay, setPickedDay] = useState<string | null>(null);
    const [hiddenUids, setHiddenUids] = useState<Set<string>>(new Set());
    const [selectedStop, setSelectedStop] = useState<SelectedStop | null>(null);
    const mapAnchor = useRef<HTMLDivElement>(null);
    const isSmallScreen = useIsSmallScreen();

    const { placedBuildings, days, day, routes } = useRoutes({ servicesData, buildings, users, groupKey, day: pickedDay });

    const serviceLabel = (key: string) => (key === 'generalComment' ? 'Kommentar' : serviceTitles[key] || key);

    // Memoised: the map pans whenever its routes or the selection change identity
    const coloured: VisibleRoute[] = useMemo(() => routes.map((route, index) => ({
        ...route,
        color: getChartColor(index),
        name: users.find(user => user.key === route.uid)?.name || 'Okänd användare',
        hidden: hiddenUids.has(route.uid),
    })), [routes, users, hiddenUids]);
    const visible = useMemo(() => coloured.filter(route => !route.hidden), [coloured]);

    // Uppspelning: one person's day replayed on the map
    const playback = useRoutePlayback();
    const timelines = useMemo(() => {
        const pointOf = (key: string): LatLng | undefined => {
            const building = placedBuildings.get(key);
            return building ? [building.coordinates.latitude, building.coordinates.longitude] : undefined;
        };
        return new Map(routes.map(route => [route.uid, routeTimeline(route.stops, pointOf)]));
    }, [routes, placedBuildings]);
    const playingRoute = visible.find(route => route.uid === playback.uid);
    const playingTimeline = playingRoute ? timelines.get(playingRoute.uid) : null;
    const replay: Replay | null = playingRoute && playingTimeline
        ? { uid: playingRoute.uid, color: playingRoute.color, running: playback.running, frame: frameAt(playingTimeline, playback.progress) }
        : null;
    const togglePlay = (uid: string) => {
        const timeline = timelines.get(uid);
        if (!timeline) return;
        if (playback.uid === uid && playback.running) {
            playback.pause();
            return;
        }
        playback.play(uid, playbackDurationMs(timeline));
        if (isSmallScreen) mapAnchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const pickGroup = (key: string) => {
        setPickedGroupKey(key);
        setPickedDay(null);
        setSelectedStop(null);
        playback.stop();
    };
    const pickDay = (next: string | null) => {
        setPickedDay(next);
        setSelectedStop(null);
        playback.stop();
    };
    const togglePerson = (uid: string) => {
        setHiddenUids(prev => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid);
            else next.add(uid);
            return next;
        });
        // The map refits to the people now shown
        setSelectedStop(null);
        if (playback.uid === uid) playback.stop();
    };
    const selectStop = (stop: SelectedStop) => {
        setSelectedStop(stop);
        if (isSmallScreen) mapAnchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const previousDay = day ? adjacentDay(days, day, -1) : null;
    const nextDay = day ? adjacentDay(days, day, 1) : null;

    const controls = (
        <div className="controls-container routes-controls">
            <div className="control-group">
                <label className="control-label" htmlFor="routes-company">Företag</label>
                <Form.Select
                    id="routes-company"
                    value={groupKey ?? ''}
                    onChange={event => pickGroup(event.target.value)}
                    disabled={sortedGroups.length === 0}
                >
                    {sortedGroups.map(group => <option key={group.key} value={group.key}>{group.name}</option>)}
                </Form.Select>
            </div>
            <div className="control-group">
                <label className="control-label" htmlFor="routes-day">Dag</label>
                <div className="routes-day-picker">
                    <Button variant="outline-secondary" aria-label="Föregående dag med besök"
                            disabled={!previousDay} onClick={() => pickDay(previousDay)}>
                        <ChevronLeft size={18} />
                    </Button>
                    <Form.Control
                        id="routes-day"
                        type="date"
                        value={day ?? ''}
                        disabled={!day}
                        onChange={event => event.target.value && pickDay(event.target.value)}
                    />
                    <Button variant="outline-secondary" aria-label="Nästa dag med besök"
                            disabled={!nextDay} onClick={() => pickDay(nextDay)}>
                        <ChevronRight size={18} />
                    </Button>
                </div>
            </div>
        </div>
    );

    const card = (content: React.ReactNode) => (
        <div className="analysis-card analysis-main-card routes-card std-box dropshadow">
            {controls}
            {content}
        </div>
    );

    if (isLoading && !servicesData) {
        return card(<div className="loading-container"><p>Laddar data...</p></div>);
    }
    if (sortedGroups.length === 0) {
        return card(<div className="empty-container"><p>Det finns inga företag att visa resvägar för.</p></div>);
    }
    if (!day) {
        return card(<div className="empty-container"><p>Företagets personal har inte registrerat några besök på byggnader med position.</p></div>);
    }

    return card(
        <>
            {routes.length === 0 ? (
                <div className="empty-container"><p>Inga besök registrerades den här dagen.</p></div>
            ) : (
                <>
                    <div className="routes-legend" aria-label="Personer">
                        {coloured.map(route => (
                            <button
                                key={route.uid}
                                type="button"
                                className={`routes-legend-item ${route.hidden ? 'off' : ''}`}
                                aria-pressed={!route.hidden}
                                onClick={() => togglePerson(route.uid)}
                            >
                                <span className="routes-swatch" style={{ backgroundColor: route.color }} />
                                {route.name}
                            </button>
                        ))}
                    </div>
                    <div className="routes-layout">
                        <div className="routes-map" ref={mapAnchor}>
                            <RoutesMap
                                routes={visible}
                                placedBuildings={placedBuildings}
                                selectedStop={selectedStop}
                                onSelectStop={selectStop}
                                replay={replay}
                                onStopReplay={playback.stop}
                            />
                        </div>
                        <RouteStopList
                            routes={visible}
                            placedBuildings={placedBuildings}
                            serviceLabel={serviceLabel}
                            selectedStop={selectedStop}
                            onSelectStop={selectStop}
                            replay={replay}
                            onTogglePlay={togglePlay}
                        />
                    </div>
                </>
            )}
            <p className="year-comparison-hint">
                Visar personal som tillhör företaget i dag. Byggnader utan position visas inte.
            </p>
        </>,
    );
};

export default RoutesView;
